import axios from "axios";
import {
  CategoryItem,
  extractCategoriesFromResponse,
  extractLastSubmittedCategory,
  getNextCategoryInSequence,
} from "./utils/categorySequence";

// Base URL configuration: set REACT_APP_API_BASE_URL in your env if available
const baseUrl = process.env.REACT_APP_API_BASE_URL || "";

export const getUserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "UTC";
  }
};

export const verifyTicketStatusApi = async (data: {
  ticketId: string;
  userTimezone?: string;
  [key: string]: unknown;
}) => {
  try {
    const response = await axios.get(`${baseUrl}/user/payment/scan-ticket`, {
      params: {
        ...data,
        userTimezone: data.userTimezone ?? getUserTimezone(),
      },
    });
    return response;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log({ error });
    throw error;
  }
};

const categoriesApiUrl = `${baseUrl}/museums/tree-of-wisdom/categories/active`;
const lastSubmittedCategoryApiUrl = `${baseUrl}/museums/tree-of-wisdom/categories/last-submitted`;
const checkContentApiUrl = `${baseUrl}/museums/tree-of-wisdom/check-content`;

export type TreeOfWisdomContentCheckResult = {
  blocked: boolean;
  matchedWord?: string | null;
  blockedBy?: string | null;
  reason?: string | null;
};

/** Dry-run moderation check — does not save a submission. */
export const checkTreeOfWisdomContentApi = async (
  text: string
): Promise<TreeOfWisdomContentCheckResult> => {
  const response = await axios.post(checkContentApiUrl, { text });
  return (response.data ?? { blocked: false }) as TreeOfWisdomContentCheckResult;
};

export const fetchTreeOfWisdomCategoriesApi = async () => {
  try {
    const response = await axios.get(categoriesApiUrl);
    return response;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log({ error });
    throw error;
  }
};

/** Returns the most recently submitted tree-of-wisdom category (museum-wide). */
export const fetchLastSubmittedCategoryApi = async () => {
  try {
    const response = await axios.get(lastSubmittedCategoryApiUrl);
    return response;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log({ error });
    throw error;
  }
};

/**
 * Loads active categories + last submitted category, then returns the next
 * category in the current API order (wraps to the first after the last).
 */
export const resolveNextCategoryInSequence = async (): Promise<CategoryItem | null> => {
  const categoriesResponse = await fetchTreeOfWisdomCategoriesApi();
  const categories = extractCategoriesFromResponse(categoriesResponse);

  if (!categories.length) {
    return null;
  }

  let lastSubmitted: CategoryItem | null = null;
  try {
    const lastResponse = await fetchLastSubmittedCategoryApi();
    lastSubmitted = extractLastSubmittedCategory(lastResponse);
  } catch (error) {
    // If the last-submitted endpoint is unavailable, start from the first category.
    console.warn(
      "Unable to fetch last submitted category; using first category in sequence.",
      error
    );
  }

  return getNextCategoryInSequence(categories, lastSubmitted);
};
